import { useState } from 'react';
import {
  keepPreviousData, useMutation, useQuery, useQueryClient, useSuspenseQuery,
} from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { formatCents, gstOnTop } from '@/lib/money';
import { dayLabel } from '@/lib/purchase-order';

import { Badge, Callout, buttonClass, inputClass } from '@/components/ui';
import { Pager } from '@/components/pager';
import { ItemPicker, centsFromText, priceBasisLabel } from '@/components/admin/purchase-order-items';
import {
  adminLocationsQuery, adminPurchaseOrdersQuery, adminSuppliersQuery, purchaseOrderQuery,
} from '@/queries';
import type {
  PoState, PurchaseOrder, PurchaseOrderDetail, PurchaseOrderLine, PurchaseOrderList,
  PurchaseOrderReceipt, SupplierItemHit,
} from '@/lib/stock-types';

/**
 * Purchase orders. Spec 003 US-4, and spec 012: purchase orders the team can
 * rely on (Guy, 29 October 2026, Parts 4 to 6).
 *
 * Make one by hand (New purchase order), or record one already placed on the
 * supplier's website (Already ordered, never emailed). Send it with a check
 * panel, attach the invoice, cancel it with a reason, see Open and Late, and
 * receive it in parts, once per delivery.
 *
 * Receiving posts to `receive_goods`, which does the whole thing in one
 * database transaction: converts the order's unit into the item's, recalculates
 * the moving average against the quantity as it stood before the delivery, and
 * moves the order's state from its lines. None of that arithmetic is repeated
 * here, because a second implementation is a second answer (P-1, P-3).
 */

/** FR-1213. The database says `sent`; the screen says Ordered, both ways. */
const STATE_LABEL: Record<PoState, string> = {
  draft: 'Draft',
  sent: 'Ordered',
  part_received: 'Part received',
  received: 'Received',
  cancelled: 'Cancelled',
};

const STATE_TONE: Record<PoState, 'ok' | 'brand' | 'warn'> = {
  draft: 'warn',
  sent: 'brand',
  part_received: 'brand',
  received: 'ok',
  cancelled: 'warn',
};

const LISTS: Array<{ key: PurchaseOrderList; label: string; hint: string }> = [
  { key: 'open', label: 'Open', hint: 'Ordered or part received.' },
  { key: 'late', label: 'Late', hint: 'Open, and the expected date has passed.' },
  { key: 'all', label: 'All', hint: 'Every order, newest first.' },
];

const KIND_LABEL = {
  supplier_invoice: 'Supplier invoice',
  packing_slip: 'Packing slip',
  order_confirmation: 'Order confirmation',
  other: 'Other',
  purchase_order: 'Purchase order sent',
} as const;

async function call(path: string, init?: RequestInit) {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : await res.json();
}

/** A JSON write. The method is in the helper's name, where the route guard reads it. */
const write = (method: 'POST' | 'PATCH') => (path: string, body: unknown) => call(path, {
  method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
});
const post = write('POST');
const patch = write('PATCH');

/** A moment as the calendar day, the same way as the order and expected dates. */
const shortDay = (at: string) =>
  new Date(at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

function stateLabel(o: Pick<PurchaseOrder, 'state' | 'cancelled_at'>) {
  if (o.state === 'received' && o.cancelled_at) return 'Received, rest cancelled';
  return STATE_LABEL[o.state];
}

/* ── New purchase order. FR-1201 to FR-1206, FR-1210, FR-1211 ─────────────── */

type DraftLine = {
  hit: SupplierItemHit;
  qty: string;
  /** Typed dollars. Empty with a suggestion means "take the suggestion". */
  price: string;
  free: boolean;
  suggested: { unit_cost_cents: number; basis: 'last_paid' | 'last_ordered'; at: string } | null;
};

function NewPurchaseOrder({
  today, costsVisible, onDone,
}: { today: string; costsVisible: boolean; onDone: (id: string) => void }) {
  const qc = useQueryClient();
  const suppliers = useQuery({ ...adminSuppliersQuery, retry: false });
  const { locations } = useSuspenseQuery(adminLocationsQuery).data;
  const [supplierId, setSupplierId] = useState('');
  const [how, setHow] = useState<'email' | 'external'>('email');
  const [supplierRef, setSupplierRef] = useState('');
  const [orderDate, setOrderDate] = useState(today);
  const [expected, setExpected] = useState('');
  const [locationId, setLocationId] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [error, setError] = useState<string | null>(null);

  const add = async (hit: SupplierItemHit) => {
    let suggested: DraftLine['suggested'] = null;
    if (costsVisible) {
      const r = await call(`/api/suppliers/${supplierId}/line-prices?items=${hit.item_id}`).catch(() => null) as
        { prices: Record<string, DraftLine['suggested']> } | null;
      suggested = r?.prices[hit.item_id] ?? null;
    }
    setLines((ls) => [...ls, {
      hit, qty: '1', price: suggested ? (suggested.unit_cost_cents / 100).toFixed(2) : '', free: false, suggested,
    }]);
  };

  const priceOf = (l: DraftLine): number | null | 'bad' => (l.free ? 0 : centsFromText(l.price));
  const exGst = lines.reduce((sum, l) => {
    const p = priceOf(l);
    return typeof p === 'number' ? sum + Math.round(Number(l.qty) * p) : sum;
  }, 0);

  const save = useMutation({
    mutationFn: async () => {
      if (!supplierId) throw new Error('Choose the supplier.');
      if (lines.length === 0) throw new Error('Add at least one item.');
      const body = {
        supplier_id: supplierId,
        order_date: orderDate,
        expected_at: expected || null,
        delivery_location_id: locationId || null,
        note: note.trim() || null,
        lines: lines.map((l) => {
          const p = priceOf(l);
          if (p === 'bad') throw new Error(`The price of ${l.hit.code} is not a price. Type it like 7.25.`);
          if (!(Number(l.qty) > 0)) throw new Error(`The quantity of ${l.hit.code} is a number more than 0.`);
          const line: Record<string, unknown> = { item_id: l.hit.item_id, qty_ordered: Number(l.qty) };
          // The suggested price untouched goes as nothing, so the server puts
          // it on with where it came from (FR-1261).
          const untouched = l.suggested && !l.free && p === l.suggested.unit_cost_cents;
          if (costsVisible && !untouched) {
            if (l.free) line.free_of_charge = true;
            else line.unit_cost_cents = p;
          }
          return line;
        }),
      };
      const made = (await post('/api/purchase-orders', body)) as { id: string };
      if (how === 'external') {
        await post(`/api/purchase-orders/${made.id}/ordered`, {
          supplier_ref: supplierRef.trim() || null, order_date: orderDate,
        }).catch((e: Error) => {
          throw new Error(`The order is saved as a draft, but not as ordered: ${e.message}`);
        });
      }
      return made.id;
    },
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'purchase-orders'] });
      onDone(id);
    },
    onError: (e: Error) => setError(e.message),
  });

  const offered = (suppliers.data?.suppliers ?? []).filter((s) => s.active);

  return (
    <div className="mb-6 rounded-[10px] border border-line bg-panel p-4">
      <h2 className="text-[16px] font-semibold">New purchase order</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-[13px] font-semibold" htmlFor="po-supplier">
          Supplier
          <select
            id="po-supplier" className={`${inputClass} mt-1 w-full`} value={supplierId}
            onChange={(e) => { setSupplierId(e.target.value); setLines([]); }}
          >
            <option value="">Choose a supplier</option>
            {offered.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <fieldset className="text-[13px]">
          <legend className="font-semibold">How it was ordered</legend>
          <label className="mt-1 flex items-center gap-2">
            <input type="radio" name="po-how" checked={how === 'email'} onChange={() => setHow('email')} />
            Send from here <span className="text-muted">The system emails the order.</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="po-how" checked={how === 'external'} onChange={() => setHow('external')} />
            Already ordered with the supplier <span className="text-muted">Nothing is emailed.</span>
          </label>
        </fieldset>
        {how === 'external' && (
          <label className="text-[13px] font-semibold" htmlFor="po-supplier-ref">
            Supplier&apos;s order reference <span className="font-normal text-muted">(optional)</span>
            <input
              id="po-supplier-ref" className={`${inputClass} mt-1 w-full`} maxLength={100}
              value={supplierRef} onChange={(e) => setSupplierRef(e.target.value)}
            />
          </label>
        )}
        <label className="text-[13px] font-semibold" htmlFor="po-order-date">
          Order date
          <input
            id="po-order-date" type="date" className={`${inputClass} mt-1 w-full`} max={today}
            value={orderDate} onChange={(e) => setOrderDate(e.target.value)}
          />
        </label>
        <label className="text-[13px] font-semibold" htmlFor="po-expected">
          Expected delivery <span className="font-normal text-muted">(optional)</span>
          <input
            id="po-expected" type="date" className={`${inputClass} mt-1 w-full`}
            value={expected} onChange={(e) => setExpected(e.target.value)}
          />
        </label>
        <label className="text-[13px] font-semibold" htmlFor="po-location">
          Deliver to
          <select
            id="po-location" className={`${inputClass} mt-1 w-full`} value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          >
            <option value="">We collect</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.code} {l.label}</option>)}
          </select>
        </label>
        <label className="text-[13px] font-semibold sm:col-span-2" htmlFor="po-note">
          Note <span className="font-normal text-muted">(optional, on the order the supplier gets)</span>
          <input id="po-note" className={`${inputClass} mt-1 w-full`} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>

      {supplierId && (
        <div className="mt-4">
          <ItemPicker supplierId={supplierId} onOrder={lines.map((l) => l.hit.item_id)} onAdd={(h) => void add(h)} />
        </div>
      )}

      {lines.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[12px] text-muted">
                <th className="py-1 pr-2 font-semibold">Our code</th>
                <th className="py-1 pr-2 font-semibold">Product</th>
                <th className="py-1 pr-2 font-semibold">Colour</th>
                <th className="py-1 pr-2 font-semibold">Size</th>
                <th className="py-1 pr-2 text-right font-semibold">Quantity</th>
                {costsVisible && <th className="py-1 pr-2 text-right font-semibold">Each, without GST</th>}
                {costsVisible && <th className="py-1 pr-2 text-right font-semibold">Line total</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const p = priceOf(l);
                const set = (patch: Partial<DraftLine>) =>
                  setLines((ls) => ls.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                return (
                  <tr key={l.hit.item_id} className="border-t border-line align-top">
                    <td className="py-1 pr-2 font-mono">{l.hit.code}</td>
                    <td className="py-1 pr-2">{l.hit.name}</td>
                    <td className="py-1 pr-2">{l.hit.colour ?? ''}</td>
                    <td className="py-1 pr-2">{l.hit.size ?? ''}</td>
                    <td className="py-1 pr-2 text-right">
                      <input
                        aria-label={`Quantity of ${l.hit.code}`} inputMode="decimal"
                        className={`${inputClass} h-8 w-[72px] text-right`} value={l.qty}
                        onChange={(e) => set({ qty: e.target.value })}
                      />
                      <span className="ml-1 text-muted">{l.hit.base_uom}</span>
                    </td>
                    {costsVisible && (
                      <td className="py-1 pr-2 text-right">
                        <input
                          aria-label={`Price each of ${l.hit.code}, in dollars`} inputMode="decimal"
                          className={`${inputClass} h-8 w-[84px] text-right`} value={l.free ? '0.00' : l.price}
                          disabled={l.free} placeholder="Blank"
                          onChange={(e) => set({ price: e.target.value })}
                        />
                        <label className="mt-1 flex items-center justify-end gap-1 text-[12px]">
                          <input type="checkbox" checked={l.free} onChange={(e) => set({ free: e.target.checked })} />
                          Free of charge
                        </label>
                        {l.suggested && !l.free && p === l.suggested.unit_cost_cents && (
                          <span className="block text-[11.5px] text-muted">
                            {priceBasisLabel(l.suggested.basis, l.suggested.at)}
                          </span>
                        )}
                        {!l.suggested && l.price === '' && !l.free && (
                          <span className="block text-[11.5px] text-muted">Never bought from this supplier</span>
                        )}
                      </td>
                    )}
                    {costsVisible && (
                      <td className="py-1 pr-2 text-right">
                        {typeof p === 'number' ? formatCents(Math.round(Number(l.qty) * p)) : ''}
                      </td>
                    )}
                    <td className="py-1 text-right">
                      <button
                        type="button" className="text-[13px] underline"
                        onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {costsVisible && <Totals exGst={exGst} />}
        </div>
      )}

      {error && <Callout tone="danger" title={error} className="mt-3" />}
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button" className={buttonClass('primary', 'md')} disabled={save.isPending}
          onClick={() => { setError(null); save.mutate(); }}
        >
          {save.isPending ? 'Saving' : how === 'external' ? 'Save as ordered' : 'Save as draft'}
        </button>
        <button type="button" className={buttonClass('secondary', 'md')} onClick={() => onDone('')}>
          Cancel
        </button>
      </div>
      <p className="mt-2 text-[12.5px] text-muted">
        {how === 'external'
          ? 'The order is recorded as ordered on the order date. Nothing goes to the supplier.'
          : 'A draft goes nowhere. Open it and select Send to email it to the supplier.'}
      </p>
    </div>
  );
}

function Totals({ exGst, blank = 0 }: { exGst: number; blank?: number }) {
  const gst = gstOnTop(exGst);
  return (
    <dl className="ml-auto mt-3 grid max-w-[320px] grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-[13px]">
      <dt className="text-muted">Total without GST</dt><dd className="text-right">{formatCents(exGst)}</dd>
      <dt className="text-muted">GST</dt><dd className="text-right">{formatCents(gst)}</dd>
      <dt className="font-semibold">Total with GST</dt><dd className="text-right font-semibold">{formatCents(exGst + gst)}</dd>
      {blank > 0 && (
        <dd className="col-span-2 text-[12px] text-danger">
          {blank} {blank === 1 ? 'line has' : 'lines have'} no price and {blank === 1 ? 'is' : 'are'} not in the total.
        </dd>
      )}
    </dl>
  );
}

/* ── One order ─────────────────────────────────────────────────────────────── */

/** A line of a draft: quantity, price and Free of charge change here. FR-1205. */
function DraftLineRow({ poId, line, costsVisible, refresh }: {
  poId: string; line: PurchaseOrderLine; costsVisible: boolean; refresh: () => void;
}) {
  const [qty, setQty] = useState(String(line.qty_ordered));
  const [price, setPrice] = useState(line.unit_cost_cents == null || line.free_of_charge
    ? '' : (line.unit_cost_cents / 100).toFixed(2));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (changes: Record<string, unknown>) =>
      patch(`/api/purchase-orders/${poId}/lines/${line.id}`, changes),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e: Error) => setError(e.message),
  });
  const remove = useMutation({
    mutationFn: () => call(`/api/purchase-orders/${poId}/lines/${line.id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });
  const typed = centsFromText(price);
  const priceChanged = !line.free_of_charge && typed !== 'bad' && typed !== (line.unit_cost_cents ?? null);

  return (
    <tr className="border-t border-line align-top">
      <td className="whitespace-nowrap py-1.5 pr-2 font-mono text-muted">{line.supplier_code ?? ''}</td>
      <td className="whitespace-nowrap py-1.5 pr-2 font-mono">{line.code}</td>
      <td className="py-1.5 pr-2">{line.product}</td>
      <td className="py-1.5 pr-2">{line.colour ?? ''}</td>
      <td className="py-1.5 pr-2">{line.size ?? ''}</td>
      <td className="py-1.5 pr-2 text-right">
        <input
          aria-label={`Quantity of ${line.code}`} inputMode="decimal" className={`${inputClass} h-8 w-[72px] text-right`}
          value={qty} onChange={(e) => setQty(e.target.value)}
          onBlur={() => { if (Number(qty) !== line.qty_ordered) save.mutate({ qty_ordered: Number(qty) }); }}
        />
        <span className="ml-1 text-muted">{line.uom}</span>
      </td>
      {costsVisible && (
        <td className="py-1.5 pr-2 text-right">
          {line.free_of_charge ? <span>Free</span> : (
            <span className="inline-flex items-center gap-1">
              <input
                aria-label={`Price each of ${line.code}, in dollars`} inputMode="decimal" placeholder="Blank"
                className={`${inputClass} h-8 w-[84px] text-right`} value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              {priceChanged && (
                <button
                  type="button" className={buttonClass('primary', 'sm')} disabled={save.isPending}
                  onClick={() => {
                    if (typed === 0) { setError('A price of $0 needs Free of charge.'); return; }
                    save.mutate({ unit_cost_cents: typed });
                  }}
                >
                  Save
                </button>
              )}
            </span>
          )}
          <label className="mt-1 flex items-center justify-end gap-1 text-[12px]">
            <input
              type="checkbox" checked={Boolean(line.free_of_charge)}
              onChange={(e) => save.mutate(e.target.checked
                ? { free_of_charge: true }
                : { free_of_charge: false, unit_cost_cents: null })}
            />
            Free of charge
          </label>
          {line.unit_cost_cents == null && <span className="block text-[11.5px] text-danger">No price</span>}
          {priceBasisLabel(line.price_basis, line.price_basis_at) && (
            <span className="block text-[11.5px] text-muted">{priceBasisLabel(line.price_basis, line.price_basis_at)}</span>
          )}
        </td>
      )}
      {costsVisible && (
        <td className="py-1.5 pr-2 text-right">{line.line_total_cents == null ? '' : formatCents(line.line_total_cents)}</td>
      )}
      <td className="py-1.5 text-right">
        <button type="button" className="text-[13px] underline" disabled={remove.isPending} onClick={() => remove.mutate()}>
          Remove
        </button>
        {error && <span role="alert" className="block text-[12px] text-danger">{error}</span>}
      </td>
    </tr>
  );
}

/**
 * The lines. FR-1250: ordered, received before and still to come on each.
 * On a draft the quantity and the price change in place.
 */
function Lines({ detail, refresh }: { detail: PurchaseOrderDetail; refresh: () => void }) {
  const { order, lines, costs_visible: costs, may_edit: mayEdit } = detail;
  const draft = order.state === 'draft' && mayEdit;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[12px] text-muted">
            <th className="py-1 pr-2 font-semibold">Supplier code</th>
            <th className="py-1 pr-2 font-semibold">Our code</th>
            <th className="py-1 pr-2 font-semibold">Product</th>
            <th className="py-1 pr-2 font-semibold">Colour</th>
            <th className="py-1 pr-2 font-semibold">Size</th>
            <th className="py-1 pr-2 text-right font-semibold">Ordered</th>
            {!draft && <th className="py-1 pr-2 text-right font-semibold">Received</th>}
            {!draft && <th className="py-1 pr-2 text-right font-semibold">Still to come</th>}
            {costs && <th className="py-1 pr-2 text-right font-semibold">Each, without GST</th>}
            {costs && <th className="py-1 pr-2 text-right font-semibold">Line total</th>}
            {draft && <th />}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            if (draft) return <DraftLineRow key={l.id} poId={order.id} line={l} costsVisible={costs} refresh={refresh} />;
            const left = Math.max(0, l.qty_ordered - l.qty_received);
            const closed = order.state === 'cancelled' || (order.state === 'received' && order.cancelled_at);
            return (
              <tr key={l.id} className="border-t border-line">
                <td className="whitespace-nowrap py-1.5 pr-2 font-mono text-muted">{l.supplier_code ?? ''}</td>
                <td className="whitespace-nowrap py-1.5 pr-2 font-mono">{l.code}</td>
                <td className="py-1.5 pr-2">{l.product}</td>
                <td className="py-1.5 pr-2">{l.colour ?? ''}</td>
                <td className="py-1.5 pr-2">{l.size ?? ''}</td>
                <td className="py-1.5 pr-2 text-right">{l.qty_ordered} {l.uom}</td>
                <td className="py-1.5 pr-2 text-right">{l.qty_received}</td>
                <td className="py-1.5 pr-2 text-right font-semibold">
                  {left === 0 ? <Badge tone="ok">done</Badge> : closed ? <span className="text-muted">Cancelled</span> : left}
                </td>
                {costs && (
                  <td className="py-1.5 pr-2 text-right">
                    {l.free_of_charge ? 'Free' : l.unit_cost_cents == null ? 'No price' : formatCents(l.unit_cost_cents)}
                  </td>
                )}
                {costs && <td className="py-1.5 pr-2 text-right">{l.line_total_cents == null ? '' : formatCents(l.line_total_cents)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!costs && (
        <p className="mt-2 text-[12.5px] text-muted">
          Prices are part of the accounts, so they show only with the costs permission.
        </p>
      )}
      {costs && detail.totals && <Totals exGst={detail.totals.ex_gst_cents} blank={detail.totals.blank_lines} />}
      {draft && (
        <div className="mt-3">
          <AddLine detail={detail} refresh={refresh} />
        </div>
      )}
    </div>
  );
}

function AddLine({ detail, refresh }: { detail: PurchaseOrderDetail; refresh: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: (hit: SupplierItemHit) =>
      post(`/api/purchase-orders/${detail.order.id}/lines`, { item_id: hit.item_id, qty_ordered: 1 }),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e: Error) => setError(e.message),
  });
  return (
    <>
      <ItemPicker
        supplierId={detail.order.supplier_id}
        onOrder={detail.lines.map((l) => l.item_id)}
        onAdd={(h) => add.mutate(h)}
      />
      {error && <p role="alert" className="mt-2 text-[13px] text-danger">{error}</p>}
    </>
  );
}

/**
 * THE SEND PANEL. FR-1220, FR-1224, spec 013 FR-1312. Shows who it goes to,
 * which staff may change for this send, the contacts staff may add, and the
 * total, before anything goes.
 */
function SendPanel({ detail, again, onClose, refresh }: {
  detail: PurchaseOrderDetail; again: boolean; onClose: () => void; refresh: () => void;
}) {
  const { send_to: sendTo, order } = detail;
  const [to, setTo] = useState(sendTo.to?.email ?? '');
  const [contacts, setContacts] = useState<string[]>([]);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const send = useMutation({
    mutationFn: () => post(`/api/purchase-orders/${order.id}/send`, {
      to: to.trim() && to.trim() !== sendTo.to?.email ? to.trim() : undefined,
      contact_ids: contacts,
    }) as Promise<{ emailed: boolean; reason?: string; to: { email: string } }>,
    onSuccess: (r) => {
      setResult(r.emailed
        ? { ok: true, text: `Emailed to ${r.to.email}, with the order as a PDF.` }
        : { ok: false, text: r.reason ?? 'The email did not go.' });
      refresh();
    },
    onError: (e: Error) => setResult({ ok: false, text: e.message }),
  });
  const source = sendTo.to?.source === 'po_email' ? 'the purchase order email' : 'the general email';

  return (
    <div className="mt-4 rounded-[10px] border border-line-strong p-4">
      <p className="text-[15px] font-semibold">{again ? 'Send again' : 'Send to the supplier'}</p>
      <label className="mt-3 block text-[13px] font-semibold" htmlFor="po-send-to">
        To
        <input
          id="po-send-to" type="email" className={`${inputClass} mt-1 block w-full max-w-[420px]`} value={to}
          onChange={(e) => setTo(e.target.value)} placeholder="orders@supplier.com.au"
        />
      </label>
      <p className="mt-1 text-[12.5px] text-muted">
        {sendTo.to
          ? `From the supplier: ${source}. A change here is for this send only.`
          : 'The supplier has no email on file. Type one for this send, or use Already ordered.'}
      </p>
      {sendTo.contacts.length > 0 && (
        <fieldset className="mt-3 text-[13px]">
          <legend className="font-semibold">Also send a copy to</legend>
          {sendTo.contacts.map((c) => (
            <label key={c.id} className="mt-1 flex items-center gap-2">
              <input
                type="checkbox" checked={contacts.includes(c.id)}
                onChange={(e) => setContacts((cs) => e.target.checked ? [...cs, c.id] : cs.filter((x) => x !== c.id))}
              />
              {c.name}{c.position ? `, ${c.position}` : ''} <span className="text-muted">{c.email}</span>
            </label>
          ))}
        </fieldset>
      )}
      <p className="mt-3 text-[13px]">
        {detail.lines.length} {detail.lines.length === 1 ? 'line' : 'lines'}
        {detail.totals ? `, ${formatCents(detail.totals.inc_gst_cents)} with GST` : ''}. The email holds the
        order and a PDF of it.
      </p>
      {result && <Callout tone={result.ok ? 'ok' : 'danger'} title={result.text} className="mt-3" />}
      <div className="mt-3 flex gap-3">
        {!result?.ok && (
          <button
            type="button" className={buttonClass('primary', 'md')} disabled={send.isPending || !to.trim()}
            onClick={() => { setResult(null); send.mutate(); }}
          >
            {send.isPending ? 'Sending' : again ? 'Send again' : 'Send'}
          </button>
        )}
        <button type="button" className={buttonClass('secondary', 'md')} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/** Already ordered with the supplier. FR-1211. */
function OrderedPanel({ detail, onClose, refresh }: {
  detail: PurchaseOrderDetail; onClose: () => void; refresh: () => void;
}) {
  const [ref, setRef] = useState('');
  const [day, setDay] = useState(detail.order.order_date ?? detail.today);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => post(`/api/purchase-orders/${detail.order.id}/ordered`, {
      supplier_ref: ref.trim() || null, order_date: day,
    }),
    onSuccess: () => { onClose(); refresh(); },
    onError: (e: Error) => setError(e.message),
  });
  return (
    <div className="mt-4 rounded-[10px] border border-line-strong p-4">
      <p className="text-[15px] font-semibold">Already ordered with the supplier</p>
      <p className="mt-1 text-[13px] text-muted">
        For an order placed on the supplier&apos;s website, by phone or in the shop. Nothing is emailed, now or later.
      </p>
      <div className="mt-3 flex flex-wrap gap-3">
        <label className="text-[13px] font-semibold" htmlFor="po-ordered-ref">
          Supplier&apos;s order reference <span className="font-normal text-muted">(optional)</span>
          <input id="po-ordered-ref" className={`${inputClass} mt-1 w-[220px]`} maxLength={100} value={ref} onChange={(e) => setRef(e.target.value)} />
        </label>
        <label className="text-[13px] font-semibold" htmlFor="po-ordered-date">
          Order date
          <input id="po-ordered-date" type="date" max={detail.today} className={`${inputClass} mt-1 w-[170px]`} value={day} onChange={(e) => setDay(e.target.value)} />
        </label>
      </div>
      {error && <Callout tone="danger" title={error} className="mt-3" />}
      <div className="mt-3 flex gap-3">
        <button type="button" className={buttonClass('primary', 'md')} disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving' : 'Save as ordered'}
        </button>
        <button type="button" className={buttonClass('secondary', 'md')} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/** Cancel. FR-1240, FR-1241. */
function CancelPanel({ detail, onClose, refresh }: {
  detail: PurchaseOrderDetail; onClose: () => void; refresh: () => void;
}) {
  const { order } = detail;
  const [reason, setReason] = useState('');
  const [told, setTold] = useState<'' | 'yes' | 'no'>('');
  const [error, setError] = useState<string | null>(null);
  const ask = order.state !== 'draft';
  const go = useMutation({
    mutationFn: () => post(`/api/purchase-orders/${order.id}/cancel`, {
      reason, supplier_told: ask ? told === 'yes' : undefined,
    }),
    onSuccess: () => { onClose(); refresh(); },
    onError: (e: Error) => setError(e.message),
  });
  return (
    <div className="mt-4 rounded-[10px] border border-line-strong p-4">
      <p className="text-[15px] font-semibold">Cancel this order</p>
      {order.state === 'part_received' && (
        <p className="mt-1 text-[13px] text-muted">
          The lines still to come close. The stock that came in stays. To send stock back, use Return on the delivery.
        </p>
      )}
      <label className="mt-3 block text-[13px] font-semibold" htmlFor="po-cancel-reason">
        Reason
        <input id="po-cancel-reason" className={`${inputClass} mt-1 w-full max-w-[420px]`} value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      {ask && (
        <fieldset className="mt-3 text-[13px]">
          <legend className="font-semibold">Did you tell the supplier?</legend>
          <label className="mr-4 inline-flex items-center gap-2">
            <input type="radio" name="po-told" checked={told === 'yes'} onChange={() => setTold('yes')} /> Yes
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="radio" name="po-told" checked={told === 'no'} onChange={() => setTold('no')} /> No
          </label>
        </fieldset>
      )}
      {error && <Callout tone="danger" title={error} className="mt-3" />}
      <div className="mt-3 flex gap-3">
        <button
          type="button" className={buttonClass('primary', 'md')}
          disabled={go.isPending || !reason.trim() || (ask && told === '')} onClick={() => go.mutate()}
        >
          {go.isPending ? 'Cancelling' : 'Cancel the order'}
        </button>
        <button type="button" className={buttonClass('secondary', 'md')} onClick={onClose}>Keep it</button>
      </div>
    </div>
  );
}

/**
 * RECEIVE. FR-1242, FR-1250 to FR-1253. The request key is made when the
 * panel opens, so a second click or a retry books the delivery once.
 */
function ReceivePanel({ detail, onClose, refresh }: {
  detail: PurchaseOrderDetail; onClose: () => void; refresh: () => void;
}) {
  const qc = useQueryClient();
  const { locations } = useSuspenseQuery(adminLocationsQuery).data;
  const { order, lines, costs_visible: costs } = detail;
  const [key] = useState(() => crypto.randomUUID());
  const [day, setDay] = useState(detail.today);
  const [locationId, setLocationId] = useState(order.delivery_location_id ?? '');
  const [rows, setRows] = useState<Record<string, { qty: string; price: string; reason: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<string[] | null>(null);

  const receive = useMutation({
    mutationFn: () => {
      const out = lines.flatMap((l) => {
        const r = rows[l.id];
        if (!r || !(Number(r.qty) > 0)) return [];
        const line: Record<string, unknown> = { po_line_id: l.id, qty: Number(r.qty) };
        if (r.reason.trim()) line.over_reason = r.reason.trim();
        if (costs && r.price.trim() !== '') {
          const c = centsFromText(r.price);
          if (c === 'bad' || c === null) throw new Error(`The price paid for ${l.code} is not a price.`);
          if (c !== l.unit_cost_cents) {
            if (c === 0) line.free_of_charge = true; else line.unit_cost_cents = c;
          }
        }
        return [line];
      });
      if (out.length === 0) throw new Error('Type how much of at least one line arrived.');
      return post(`/api/purchase-orders/${order.id}/receipts`, {
        location_id: locationId, received_on: day, request_key: key, lines: out,
      }) as Promise<{ notes: string[] }>;
    },
    onSuccess: (r) => {
      setError(null);
      setNotes(r.notes);
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      refresh();
      if (r.notes.length === 0) onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (notes) {
    return (
      <div className="mt-4 rounded-[10px] border border-line-strong p-4">
        <Callout tone="ok" title="The delivery is booked in.">
          {notes.map((n) => <p key={n}>{n}</p>)}
        </Callout>
        <button type="button" className={`${buttonClass('secondary', 'md')} mt-3`} onClick={onClose}>Close</button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-[10px] border border-line-strong p-4">
      <p className="text-[15px] font-semibold">Book in a delivery</p>
      <p className="mt-1 text-[13px] text-muted">
        Type what arrived, in the unit of the order. The stock and the average cost follow from it.
      </p>
      <div className="mt-3 flex flex-wrap gap-3">
        <label className="text-[13px] font-semibold" htmlFor="po-recv-date">
          Date of the delivery
          <input id="po-recv-date" type="date" max={detail.today} className={`${inputClass} mt-1 w-[170px]`} value={day} onChange={(e) => setDay(e.target.value)} />
        </label>
        <label className="text-[13px] font-semibold" htmlFor="po-recv-location">
          Onto which location
          <select
            id="po-recv-location" className={`${inputClass} mt-1 w-[280px]`} value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          >
            <option value="">Choose a location</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id} disabled={Boolean(l.locked_by_take_id)}>
                {l.code} {l.label}{l.locked_by_take_id ? ', being counted' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[12px] text-muted">
              <th className="py-1 pr-2 font-semibold">Item</th>
              <th className="py-1 pr-2 text-right font-semibold">Ordered</th>
              <th className="py-1 pr-2 text-right font-semibold">Received before</th>
              <th className="py-1 pr-2 text-right font-semibold">Receive now</th>
              <th className="py-1 pr-2 text-right font-semibold">Still to come</th>
              {costs && <th className="py-1 pr-2 text-right font-semibold">Price paid each</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const r = rows[l.id] ?? { qty: '', price: '', reason: '' };
              const set = (patch: Partial<typeof r>) => setRows((rs) => ({ ...rs, [l.id]: { ...r, ...patch } }));
              const now = Number(r.qty) || 0;
              const left = l.qty_ordered - l.qty_received - now;
              return (
                <tr key={l.id} className="border-t border-line align-top">
                  <td className="py-1.5 pr-2">
                    <span className="font-mono">{l.code}</span> {l.product}
                    {[l.colour, l.size].filter(Boolean).length > 0 && (
                      <span className="text-muted">, {[l.colour, l.size].filter(Boolean).join(', ')}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2 text-right">{l.qty_ordered} {l.uom}</td>
                  <td className="py-1.5 pr-2 text-right">{l.qty_received}</td>
                  <td className="py-1.5 pr-2 text-right">
                    <input
                      aria-label={`Receive now of ${l.code}`} inputMode="decimal" className={`${inputClass} h-8 w-[80px] text-right`}
                      value={r.qty} onChange={(e) => set({ qty: e.target.value })}
                    />
                    {left < 0 && (
                      <span className="mt-1 block">
                        <span className="block text-[12px] font-semibold text-danger">More than ordered</span>
                        <input
                          aria-label={`Reason for more than ordered of ${l.code}`} placeholder="Reason"
                          className={`${inputClass} mt-1 h-8 w-[180px]`} value={r.reason}
                          onChange={(e) => set({ reason: e.target.value })}
                        />
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2 text-right">{Math.max(0, left)}</td>
                  {costs && (
                    <td className="py-1.5 pr-2 text-right">
                      <input
                        aria-label={`Price paid each of ${l.code}, in dollars`} inputMode="decimal"
                        className={`${inputClass} h-8 w-[84px] text-right`}
                        placeholder={l.free_of_charge ? 'Free' : l.unit_cost_cents == null ? 'Blank' : (l.unit_cost_cents / 100).toFixed(2)}
                        value={r.price} onChange={(e) => set({ price: e.target.value })}
                      />
                      <span className="block text-[11.5px] text-muted">Empty is the order price</span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {error && <Callout tone="danger" title={error} className="mt-3" />}
      <div className="mt-3 flex gap-3">
        <button
          type="button" className={buttonClass('primary', 'md')} disabled={!locationId || receive.isPending}
          onClick={() => receive.mutate()}
        >
          {receive.isPending ? 'Booking in' : 'Book it in'}
        </button>
        <button type="button" className={buttonClass('secondary', 'md')} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/**
 * One delivery, its lines, freight, and the way to undo a line. T091, P-2,
 * FR-1255, FR-1272. Freight lands on what remains, not on what arrived.
 */
function Receipt({ receipt, costsVisible, mayEdit, refresh }: {
  receipt: PurchaseOrderReceipt; costsVisible: boolean; mayEdit: boolean; refresh: () => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<'freight' | string | null>(null);
  const [kind, setKind] = useState('freight');
  const [amount, setAmount] = useState('');
  const [allocation, setAllocation] = useState<'by_value' | 'by_qty'>('by_value');
  const [back, setBack] = useState({ qty: '', reason: 'returned', note: '' });
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const freight = useMutation({
    mutationFn: () => post(`/api/receipts/${receipt.id}/landed-costs`, {
      kind, amount_cents: Math.round(Number(amount) * 100), allocation,
    }),
    onSuccess: (res) => {
      setError(null); setAmount(''); setOpen(null);
      const n = (res as { items_touched?: number } | null)?.items_touched ?? 0;
      setNote(`Spread across ${n} ${n === 1 ? 'item' : 'items'}, over what is still on the shelf. `
        + 'Stock already used keeps the cost it was used at.');
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
    },
    onError: (e: Error) => { setNote(null); setError(e.message); },
  });
  const reverse = useMutation({
    mutationFn: (lineId: string) => post(`/api/receipt-lines/${lineId}/reverse`, {
      qty: Number(back.qty), reason: back.reason, note: back.note,
    }),
    onSuccess: () => {
      setError(null); setOpen(null); setBack({ qty: '', reason: 'returned', note: '' });
      setNote('The stock is taken out. The average cost does not change.');
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="mb-2 rounded-[10px] border border-line px-3 py-2 text-[13px]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>
          <span className="font-semibold">{shortDay(receipt.received_at)}</span> into{' '}
          <span className="text-muted">{receipt.locations?.code}</span>
          {receipt.received_by_name && <span className="text-muted">, by {receipt.received_by_name}</span>}
        </span>
        {costsVisible && mayEdit && (
          <button type="button" className="text-[13px] underline" onClick={() => setOpen(open === 'freight' ? null : 'freight')}>
            {open === 'freight' ? 'Close' : 'Add freight or duty'}
          </button>
        )}
      </div>
      <ul className="mt-1">
        {receipt.lines.map((l) => (
          <li key={l.id} className="flex flex-wrap items-center gap-x-3">
            <span className="font-mono">{l.code}</span>
            <span>{l.qty}</span>
            {l.unit_cost_cents != null && <span className="text-muted">at {formatCents(l.unit_cost_cents)} each</span>}
            {l.taken_back > 0 && <span className="text-muted">{l.taken_back} taken back</span>}
            {l.over_reason && <span className="text-muted">More than ordered: {l.over_reason}</span>}
            {l.no_prior_cost && <span className="text-muted">Earlier stock had no cost. The average is this delivery&apos;s price.</span>}
            {mayEdit && l.qty - l.taken_back > 0 && (
              <button type="button" className="underline" onClick={() => setOpen(open === l.id ? null : l.id)}>
                {open === l.id ? 'Close' : 'Return or correct'}
              </button>
            )}
            {open === l.id && (
              <form
                className="mt-2 flex basis-full flex-wrap items-end gap-2"
                onSubmit={(e) => { e.preventDefault(); reverse.mutate(l.id); }}
              >
                <select
                  aria-label="What happened" className={`${inputClass} h-8`} value={back.reason}
                  onChange={(e) => setBack({ ...back, reason: e.target.value })}
                >
                  <option value="returned">Return to the supplier</option>
                  <option value="corrected">Correct a wrong receipt</option>
                </select>
                <input
                  aria-label="Quantity" inputMode="decimal" placeholder={`Up to ${l.qty - l.taken_back}`}
                  className={`${inputClass} h-8 w-[100px]`} value={back.qty}
                  onChange={(e) => setBack({ ...back, qty: e.target.value })}
                />
                <input
                  aria-label="Why" placeholder="Why" className={`${inputClass} h-8 w-[220px]`} value={back.note}
                  onChange={(e) => setBack({ ...back, note: e.target.value })}
                />
                <button type="submit" className={buttonClass('primary', 'sm')} disabled={reverse.isPending}>Take it out</button>
              </form>
            )}
          </li>
        ))}
      </ul>
      {open === 'freight' && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (Number(amount) > 0) freight.mutate(); }}
          className="mt-3 flex flex-wrap items-end gap-3"
        >
          <label className="text-[13px]">
            What
            <select value={kind} onChange={(e) => setKind(e.target.value)} className={`${inputClass} mt-1 block`}>
              <option value="freight">Freight</option>
              <option value="duty">Duty</option>
              <option value="handling">Handling</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="text-[13px]">
            How much, without GST
            <input
              type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="200.00" className={`${inputClass} mt-1 block w-32 text-right`}
            />
          </label>
          <label className="text-[13px]">
            Spread it
            <select
              value={allocation} onChange={(e) => setAllocation(e.target.value as 'by_value' | 'by_qty')}
              className={`${inputClass} mt-1 block`}
            >
              <option value="by_value">By what each line cost</option>
              <option value="by_qty">By how much of each arrived</option>
            </select>
          </label>
          <button type="submit" disabled={!(Number(amount) > 0) || freight.isPending} className={buttonClass('primary', 'sm')}>
            {freight.isPending ? 'Adding' : 'Add it'}
          </button>
        </form>
      )}
      {error && <p role="alert" className="mt-2 text-[13px] text-danger">{error}</p>}
      {note && <p className="mt-2 text-[13px] text-muted">{note}</p>}
    </div>
  );
}

/** Attachments. FR-1230 to FR-1233. */
function Attachments({ detail, refresh }: { detail: PurchaseOrderDetail; refresh: () => void }) {
  const [kind, setKind] = useState('supplier_invoice');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const add = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Choose a file.');
      const form = new FormData();
      form.set('file', file);
      form.set('kind', kind);
      return call(`/api/purchase-orders/${detail.order.id}/attachments`, { method: 'POST', body: form });
    },
    onSuccess: () => { setError(null); setFile(null); refresh(); },
    onError: (e: Error) => setError(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => call(`/api/purchase-order-attachments/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });
  const live = detail.attachments.filter((a) => !a.removed_at);
  const gone = detail.attachments.filter((a) => a.removed_at);

  return (
    <section className="mt-5">
      <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-muted">Attachments</p>
      {live.length === 0 && <p className="text-[13px] text-muted">No file yet.</p>}
      <ul className="text-[13px]">
        {live.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-x-3 border-b border-line py-1.5 last:border-0">
            <a className="font-semibold underline" href={`/api/purchase-order-attachments/${a.id}/file`} target="_blank" rel="noreferrer">
              {a.file_name}
            </a>
            <span>{KIND_LABEL[a.kind]}</span>
            {/* Spec 017 FR-1722: the file goes on the receipt's bill when the bill is sent. */}
            {a.kind === 'supplier_invoice' && (a.xero_sent_at
              ? <Badge tone="ok">On the bill in Xero</Badge>
              : <Badge tone="warn">Not sent to Xero</Badge>)}
            <span className="text-muted">
              {a.added_by_name ? `${a.added_by_name}, ` : ''}{shortDay(a.added_at)}
            </span>
            {detail.may_edit && a.kind !== 'purchase_order' && (
              <button type="button" className="underline" disabled={remove.isPending} onClick={() => remove.mutate(a.id)}>
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
      {gone.length > 0 && (
        <button type="button" className="mt-1 text-[12.5px] underline" onClick={() => setShowRemoved(!showRemoved)}>
          {showRemoved ? 'Hide' : 'Show'} {gone.length} removed
        </button>
      )}
      {showRemoved && (
        <ul className="mt-1 text-[12.5px] text-muted">
          {gone.map((a) => (
            <li key={a.id}>
              {a.file_name}, removed by {a.removed_by_name ?? 'somebody'} on {new Date(a.removed_at!).toLocaleDateString('en-AU')}
            </li>
          ))}
        </ul>
      )}
      {detail.may_edit && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-[13px]" htmlFor="po-attach-kind">
            What it is
            <select id="po-attach-kind" className={`${inputClass} mt-1 block`} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="supplier_invoice">Supplier invoice</option>
              <option value="packing_slip">Packing slip</option>
              <option value="order_confirmation">Order confirmation</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="text-[13px]" htmlFor="po-attach-file">
            File: PDF, JPEG, PNG or HEIC, to 20 MB
            <input
              id="po-attach-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/jpeg,image/png,image/heic"
              className="mt-1 block text-[13px]" onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <button type="button" className={buttonClass('secondary', 'sm')} disabled={!file || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? 'Adding' : 'Attach'}
          </button>
        </div>
      )}
      {error && <p role="alert" className="mt-2 text-[13px] text-danger">{error}</p>}
      <p className="mt-2 text-[12px] text-muted">A file changes no quantity, no cost and nothing in Xero.</p>
    </section>
  );
}

/**
 * The expected date and the note. They change after the order goes, because
 * a supplier confirms a new date, and Late reads the expected date (FR-1243).
 */
function ChangeDates({ detail, refresh }: { detail: PurchaseOrderDetail; refresh: () => void }) {
  const o = detail.order;
  const [open, setOpen] = useState(false);
  const [expected, setExpected] = useState(o.expected_at ?? '');
  const [note, setNote] = useState(o.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => patch(`/api/purchase-orders/${o.id}`, {
      expected_at: expected || null, note: note.trim() || null,
    }),
    onSuccess: () => { setError(null); setOpen(false); refresh(); },
    onError: (e: Error) => setError(e.message),
  });
  if (!open) {
    return (
      <button type="button" className="mt-2 text-[13px] underline" onClick={() => setOpen(true)}>
        Change the expected date or the note
      </button>
    );
  }
  return (
    <div className="mt-3 flex flex-wrap items-end gap-3">
      <label className="text-[13px] font-semibold" htmlFor={`po-exp-${o.id}`}>
        Expected delivery
        <input id={`po-exp-${o.id}`} type="date" className={`${inputClass} mt-1 block w-[170px]`} value={expected} onChange={(e) => setExpected(e.target.value)} />
      </label>
      <label className="text-[13px] font-semibold" htmlFor={`po-note-${o.id}`}>
        Note
        <input id={`po-note-${o.id}`} className={`${inputClass} mt-1 block w-[260px]`} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <button type="button" className={buttonClass('primary', 'sm')} disabled={save.isPending} onClick={() => save.mutate()}>Save</button>
      <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setOpen(false)}>Close</button>
      {error && <p role="alert" className="basis-full text-[13px] text-danger">{error}</p>}
    </div>
  );
}

function Detail({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const q = useQuery(purchaseOrderQuery(orderId));
  const [panel, setPanel] = useState<null | 'send' | 'again' | 'ordered' | 'cancel' | 'receive'>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'purchase-orders'] });
  };

  if (q.isError) return <p className="border-t border-line p-4 text-[14px]">{(q.error as Error).message}</p>;
  if (!q.data) return <p className="border-t border-line p-4 text-[14px] text-muted">Loading the order.</p>;
  const d = q.data;
  const o = d.order;
  const open = o.state === 'sent' || o.state === 'part_received';

  return (
    <div className="border-t border-line p-4">
      <dl className="grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-[150px_1fr]">
        <dt className="text-muted">How it was ordered</dt>
        <dd>{o.placed_via === 'external' ? "Ordered on the supplier's website, not emailed" : 'Sent from here by email'}</dd>
        {o.supplier_ref && <><dt className="text-muted">Supplier&apos;s reference</dt><dd>{o.supplier_ref}</dd></>}
        <dt className="text-muted">Order date</dt><dd>{dayLabel(o.order_date)}</dd>
        <dt className="text-muted">Expected</dt>
        <dd>
          {o.expected_at ? dayLabel(o.expected_at) : 'Not given'}
          {o.days_late != null && <Badge tone="danger" className="ml-2">{o.days_late} {o.days_late === 1 ? 'day' : 'days'} late</Badge>}
        </dd>
        <dt className="text-muted">Deliver to</dt>
        <dd>{o.delivery_location ? `${o.delivery_location.site ?? ''} ${o.delivery_location.code}`.trim() : 'We collect'}</dd>
        {o.note && <><dt className="text-muted">Note</dt><dd>{o.note}</dd></>}
        {o.cancelled_at && (
          <>
            <dt className="text-muted">{o.state === 'cancelled' ? 'Cancelled' : 'Rest cancelled'}</dt>
            <dd>
              {new Date(o.cancelled_at).toLocaleDateString('en-AU')}: {o.cancel_reason}
              {o.supplier_told != null && <span className="text-muted">. Supplier told: {o.supplier_told ? 'yes' : 'no'}</span>}
            </dd>
          </>
        )}
      </dl>

      {d.may_edit && (o.state === 'draft' || open) && <ChangeDates detail={d} refresh={refresh} />}

      <div className="mt-4"><Lines detail={d} refresh={refresh} /></div>

      {d.may_edit && (
        <div className="mt-4 flex flex-wrap gap-3">
          {o.state === 'draft' && (
            <>
              <button type="button" className={buttonClass('primary', 'md')} onClick={() => setPanel('send')}>Send</button>
              <button type="button" className={buttonClass('secondary', 'md')} onClick={() => setPanel('ordered')}>
                Already ordered with the supplier
              </button>
            </>
          )}
          {open && (
            <button type="button" className={buttonClass('primary', 'md')} onClick={() => setPanel('receive')}>Receive</button>
          )}
          {o.state === 'sent' && o.placed_via === 'email' && (
            <button type="button" className={buttonClass('secondary', 'md')} onClick={() => setPanel('again')}>Send again</button>
          )}
          {(o.state === 'draft' || open) && (
            <button type="button" className={buttonClass('secondary', 'md')} onClick={() => setPanel('cancel')}>Cancel order</button>
          )}
        </div>
      )}

      {(panel === 'send' || panel === 'again') && (
        <SendPanel detail={d} again={panel === 'again'} onClose={() => setPanel(null)} refresh={refresh} />
      )}
      {panel === 'ordered' && <OrderedPanel detail={d} onClose={() => setPanel(null)} refresh={refresh} />}
      {panel === 'cancel' && <CancelPanel detail={d} onClose={() => setPanel(null)} refresh={refresh} />}
      {panel === 'receive' && <ReceivePanel detail={d} onClose={() => setPanel(null)} refresh={refresh} />}

      {d.receipts.length > 0 && (
        <section className="mt-5">
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-muted">Deliveries</p>
          {d.receipts.map((r) => (
            <Receipt key={r.id} receipt={r} costsVisible={d.costs_visible} mayEdit={d.may_edit} refresh={refresh} />
          ))}
        </section>
      )}

      <Attachments detail={d} refresh={refresh} />

      {d.sends.length > 0 && (
        <section className="mt-5">
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-muted">Emails</p>
          <ul className="text-[13px]">
            {d.sends.map((s) => (
              <li key={s.id} className="border-b border-line py-1.5 last:border-0">
                {new Date(s.at).toLocaleString('en-AU')} to {s.recipient}
                {s.also.length > 0 && `, copy to ${s.also.join(', ')}`}
                {s.sent_by_name && <span className="text-muted">, by {s.sent_by_name}</span>}
                {': '}
                {s.result === 'sent' ? <Badge tone="ok">sent</Badge> : <Badge tone="danger">failed</Badge>}
                {s.reason && <span className="block text-muted">{s.reason}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default function AdminPurchaseOrdersPage() {
  const search = useSearch({ strict: false }) as { list?: PurchaseOrderList; open?: string };
  const navigate = useNavigate();
  const list: PurchaseOrderList = search.list ?? 'open';
  const [page, setPage] = useState(1);
  const { data, isFetching } = useQuery({
    ...adminPurchaseOrdersQuery(list, page),
    placeholderData: keepPreviousData,
  });
  const orders = data?.rows ?? [];
  const [openId, setOpenId] = useState<string | null>(search.open ?? null);
  const [making, setMaking] = useState(false);
  const costs = data?.costs_visible ?? false;
  const choose = (next: PurchaseOrderList) => {
    setPage(1);
    void navigate({ to: '/admin/purchase-orders', search: { list: next } });
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Purchase orders</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            What is on order, what is late, what has arrived, and what is still to come.
          </p>
        </div>
        {!making && (
          <button type="button" className={buttonClass('primary', 'md')} onClick={() => setMaking(true)}>
            New purchase order
          </button>
        )}
      </div>

      {making && (
        <NewPurchaseOrder
          today={data?.today ?? new Date().toLocaleDateString('en-CA')}
          costsVisible={costs}
          onDone={(id) => {
            setMaking(false);
            if (id) { setOpenId(id); choose('all'); }
          }}
        />
      )}

      <div role="tablist" aria-label="Which orders" className="mb-3 flex gap-2">
        {LISTS.map((l) => (
          <button
            key={l.key} type="button" role="tab" aria-selected={list === l.key} title={l.hint}
            className={buttonClass(list === l.key ? 'primary' : 'secondary', 'sm')}
            onClick={() => choose(l.key)}
          >
            {l.label}
          </button>
        ))}
      </div>

      {orders.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center">
          <p className="text-[16px] font-semibold">
            {list === 'late' ? 'No order is late.' : list === 'open' ? 'No order is open.' : 'No purchase orders yet.'}
          </p>
          <p className="mx-auto mt-2 max-w-[52ch] text-[14px] text-muted">
            {list === 'all'
              ? 'Select New purchase order, or raise drafts from Reorder.'
              : 'An order is open from the time it is ordered until all of it arrives or the rest is cancelled.'}
            {' '}Stock arrives only through an order, which makes every unit on the shelf traceable to what it cost.
          </p>
        </div>
      ) : (
        <div className="rounded-[10px] border border-line bg-panel">
          {orders.map((o) => (
            <div key={o.id} className="border-b border-line last:border-0">
              <button
                type="button"
                onClick={() => setOpenId(openId === o.id ? null : o.id)}
                className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left"
                aria-expanded={openId === o.id}
              >
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-mono text-[13px] font-bold">{o.ref}</span>
                  <span className="font-semibold">{o.suppliers?.name}</span>
                  {o.supplier_ref && <span className="text-[13px] text-muted">Their ref {o.supplier_ref}</span>}
                </span>
                <span className="flex flex-wrap items-center gap-3 text-[13px]">
                  <span className="text-muted">Order date {dayLabel(o.order_date)}</span>
                  {o.expected_at && <span className="text-muted">Expected {dayLabel(o.expected_at)}</span>}
                  {o.days_late != null && (
                    <Badge tone="danger">{o.days_late} {o.days_late === 1 ? 'day' : 'days'} late</Badge>
                  )}
                  {o.value_outstanding_cents !== undefined && (
                    <span>{formatCents(o.value_outstanding_cents)} to come</span>
                  )}
                  <Badge tone={STATE_TONE[o.state]}>{stateLabel(o)}</Badge>
                </span>
              </button>
              {openId === o.id && <Detail orderId={o.id} />}
            </div>
          ))}
          <Pager
            page={data?.page ?? 1}
            pages={data?.pages ?? 1}
            total={data?.total ?? 0}
            limit={data?.limit ?? 50}
            noun="purchase order"
            busy={isFetching}
            onPage={(n) => { setPage(n); window.scrollTo({ top: 0 }); }}
          />
        </div>
      )}
    </div>
  );
}
